import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowUpRight,
  FiHeart,
  FiImage,
  FiMaximize2,
  FiRefreshCw,
  FiUser,
  FiX,
} from 'react-icons/fi';
import './showcase-page.css';

const GALLERY_ENDPOINT = '/my-page/api/public/projects/gallery';
const LIKES_ENDPOINT = '/my-page/api/public/projects/gallery/likes';
const REQUEST_TIMEOUT_MS = 15000;
const MAX_CARD_ANIMATION_ORDER = 10;
const MAX_PROJECT_TITLE_LENGTH = 160;
const MAX_CREATOR_NAME_LENGTH = 80;
const PROJECT_ID_PATTERN = /^project-[1-9]\d*(?:-[23])?$/;
const THUMBNAIL_URL_PATTERN = /^\/my-page\/uploads\/[A-Za-z0-9][A-Za-z0-9._-]{0,254}$/;

const withoutKey = (record, key) => {
  if (!Object.prototype.hasOwnProperty.call(record, key)) return record;
  const next = { ...record };
  delete next[key];
  return next;
};

const toCount = (value, fallback = 0) => {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, count) : fallback;
};

const safeHttpsUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;

  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
};

const normalizeProject = (project) => {
  try {
    if (!project || typeof project !== 'object' || Array.isArray(project)) return null;
    if (typeof project.id !== 'string' || !PROJECT_ID_PATTERN.test(project.id)) return null;
    if (project.is_live !== true) return null;

    if (typeof project.title !== 'string' || typeof project.creator !== 'string') return null;
    const title = project.title.trim();
    const creator = project.creator.trim();
    if (!title || title.length > MAX_PROJECT_TITLE_LENGTH) return null;
    if (!creator || creator.length > MAX_CREATOR_NAME_LENGTH) return null;

    const launchUrl = safeHttpsUrl(project.launch_url);
    if (!launchUrl) return null;
    if (!Number.isSafeInteger(project.like_count) || project.like_count < 0) return null;
    if (typeof project.showcase_added_at !== 'string') return null;
    const publishedAt = Date.parse(project.showcase_added_at);
    if (!Number.isFinite(publishedAt)) return null;

    return {
      id: project.id,
      title,
      creator,
      thumbnail_url: typeof project.thumbnail_url === 'string'
        && THUMBNAIL_URL_PATTERN.test(project.thumbnail_url)
        ? project.thumbnail_url
        : null,
      launch_url: launchUrl,
      like_count: project.like_count,
      showcase_added_at: project.showcase_added_at,
    };
  } catch {
    return null;
  }
};

const isValidLikeResult = (result, projectId) => (
  result
  && typeof result === 'object'
  && !Array.isArray(result)
  && result.project_id === projectId
  && result.is_liked === true
  && typeof result.already_liked === 'boolean'
  && Number.isSafeInteger(result.like_count)
  && result.like_count >= 0
);

const createTimedController = () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  return { controller, timeoutId };
};

const fetchJson = async (url, signal) => {
  const response = await fetch(url, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Gallery request failed with status ${response.status}`);
  }

  return response.json();
};

const useRevealOnScroll = () => {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    if (typeof window.IntersectionObserver !== 'function') {
      element.classList.add('is-visible');
      return undefined;
    }

    const observer = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      element.classList.add('is-visible');
      observer.disconnect();
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01,
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return elementRef;
};

const ThumbnailPreview = ({
  project,
  onClose,
  backgroundRef,
  returnFocusRef,
}) => {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    const modalElement = modalRef.current;
    const backgroundElement = backgroundRef.current;
    const previousInert = backgroundElement?.getAttribute('inert') ?? null;
    const previousAriaHidden = backgroundElement?.getAttribute('aria-hidden') ?? null;

    document.body.style.overflow = 'hidden';
    if (typeof modalElement?.showModal === 'function') {
      if (!modalElement.open) modalElement.showModal();
    } else {
      modalElement?.setAttribute('open', '');
    }
    closeButtonRef.current?.focus();
    backgroundElement?.setAttribute('inert', '');
    backgroundElement?.setAttribute('aria-hidden', 'true');

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        closeButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;

      if (modalElement?.open && typeof modalElement.close === 'function') {
        modalElement.close();
      } else {
        modalElement?.removeAttribute('open');
      }

      if (backgroundElement) {
        if (previousInert === null) backgroundElement.removeAttribute('inert');
        else backgroundElement.setAttribute('inert', previousInert);

        if (previousAriaHidden === null) backgroundElement.removeAttribute('aria-hidden');
        else backgroundElement.setAttribute('aria-hidden', previousAriaHidden);
      }

      const focusTarget = returnFocusRef.current || previouslyFocused;
      returnFocusRef.current = null;
      if (focusTarget instanceof HTMLElement && focusTarget.isConnected) {
        focusTarget.focus();
      }
    };
  }, [backgroundRef, onClose, returnFocusRef]);

  const titleId = `showcase-preview-${project.id}`;

  return (
    <dialog
      ref={modalRef}
      className="showcase-preview"
      aria-labelledby={titleId}
      aria-modal="true"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="showcase-preview__dialog"
      >
        <header className="showcase-preview__header">
          <div>
            <span>THUMBNAIL PREVIEW</span>
            <h2 id={titleId}>{project.title}</h2>
            <p>{project.creator}</p>
          </div>
          <button
            ref={closeButtonRef}
            className="showcase-preview__close"
            type="button"
            aria-label={`${project.title} 크게 보기 닫기`}
            onClick={onClose}
          >
            <FiX aria-hidden="true" />
          </button>
        </header>

        <div className="showcase-preview__image">
          {imageFailed ? (
            <div className="showcase-preview__unavailable" role="status">
              <FiImage aria-hidden="true" />
              <p>이미지를 불러오지 못했습니다.</p>
            </div>
          ) : (
            <img
              src={project.thumbnail_url}
              alt={`${project.title} 크게 보기`}
              decoding="async"
              referrerPolicy="no-referrer"
              onError={() => setImageFailed(true)}
            />
          )}
        </div>
      </section>
    </dialog>
  );
};

const ProjectCard = ({
  project,
  position,
  isLiked,
  isPending,
  error,
  notice,
  onLike,
  onPreview,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const title = project.title || '학생이 만든 새로운 프로그램';
  const creator = project.creator || '나니아랩 학생';
  const likeCount = toCount(project.like_count);
  const showImage = Boolean(project.thumbnail_url) && !imageFailed;
  const buttonState = isLiked ? 'liked' : error ? 'error' : 'ready';
  const likeLabel = buttonState === 'liked'
    ? `${title} 좋아요 완료, 현재 ${likeCount}개`
    : buttonState === 'error'
      ? `${title} 좋아요 다시 시도, 현재 ${likeCount}개`
      : `${title} 좋아요, 현재 ${likeCount}개`;
  useEffect(() => {
    setImageFailed(false);
  }, [project.thumbnail_url]);

  return (
    <article
      className="showcase-card"
      role="listitem"
      style={{ '--showcase-card-order': Math.min(position, MAX_CARD_ANIMATION_ORDER) }}
    >
      {showImage ? (
        <button
          className="showcase-card__media"
          type="button"
          aria-label={`${title} 썸네일 크게 보기`}
          onClick={(event) => onPreview(project, event.currentTarget)}
        >
          <img
            src={project.thumbnail_url}
            alt={`${title} 미리보기`}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
          />
          <span className="showcase-card__zoom" aria-hidden="true">
            <FiMaximize2 />
            크게 보기
          </span>
        </button>
      ) : (
        <div className="showcase-card__media showcase-card__media--fallback">
          <div className="showcase-card__fallback" aria-hidden="true">
            <FiImage />
            <span>MADE BY STUDENT</span>
          </div>
        </div>
      )}

      <div className="showcase-card__body">
        <h3>{title}</h3>
        <div className="showcase-card__utility">
          <p className="showcase-card__creator">
            <FiUser aria-hidden="true" />
            <span>{creator}</span>
          </p>

          <div
            className="showcase-card__actions"
            role="group"
            aria-label={`${title} 반응 및 체험`}
          >
            <button
              className={`showcase-like-button${isLiked ? ' is-liked' : ''}${error ? ' has-error' : ''}`}
              type="button"
              title={isLiked ? '좋아요 완료' : error ? '좋아요 다시 시도' : '좋아요'}
              aria-label={likeLabel}
              aria-pressed={isLiked}
              aria-busy={isPending || undefined}
              disabled={isPending || isLiked}
              onClick={() => onLike(project.id)}
            >
              <FiHeart aria-hidden="true" />
              <strong>{likeCount}</strong>
            </button>

            <a
              className="showcase-card__launch"
              href={project.launch_url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${title} 체험하기, 새 창에서 열림`}
            >
              체험하기
              <FiArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </div>

        {error ? (
          <p className="showcase-card__feedback is-error" role="alert">
            좋아요를 저장하지 못했습니다. 다시 눌러 주세요.
          </p>
        ) : null}
        {notice ? (
          <p className="showcase-card__feedback" role="status">
            {notice}
          </p>
        ) : null}
      </div>
    </article>
  );
};

const ShowcaseSection = ({
  id,
  title,
  description,
  projects,
  likedProjectIds,
  likePending,
  likeErrors,
  likeNotices,
  onLike,
  indexLabel,
  metaLabel,
  onPreview,
}) => {
  const sectionRef = useRevealOnScroll();

  return (
    <section
      ref={sectionRef}
      id={id}
      className="showcase-section showcase-section--all"
      aria-labelledby={`${id}-title`}
    >
      <div className="showcase-shell">
        <header className="showcase-section__header">
          <div className="showcase-section__heading">
            <span className="showcase-section__index" aria-hidden="true">
              <b>{indexLabel}</b>
              <i />
              {metaLabel}
            </span>
            <h2 id={`${id}-title`}>{title}</h2>
          </div>
          <p>{description}</p>
        </header>

        {projects.length > 0 ? (
          <div className="showcase-grid showcase-grid--all" role="list" aria-label={`${title} 작품 목록`}>
            {projects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                position={index + 1}
                isLiked={likedProjectIds.has(project.id)}
                isPending={Boolean(likePending[project.id])}
                error={likeErrors[project.id]}
                notice={likeNotices[project.id]}
                onLike={onLike}
                onPreview={onPreview}
              />
            ))}
          </div>
        ) : (
          <div className="showcase-section__empty" role="status">
            <span aria-hidden="true">—</span>
            <p>아직 이 목록에 오른 작품이 없습니다.</p>
          </div>
        )}
      </div>
    </section>
  );
};

const LoadingState = () => (
  <section className="showcase-state showcase-state--loading" aria-label="작품 로딩 상태">
    <div className="showcase-shell">
      <div className="showcase-loading-copy" role="status" aria-live="polite">
        <span className="showcase-sr-only">작품을 불러오는 중입니다.</span>
        <i aria-hidden="true" />
        <i aria-hidden="true" />
      </div>
      <div className="showcase-skeleton-grid" aria-hidden="true">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index}><span /><i /><b /></div>
        ))}
      </div>
    </div>
  </section>
);

const ErrorState = ({ onRetry }) => (
  <section className="showcase-state" aria-label="작품 불러오기 오류">
    <div className="showcase-state__panel">
      <FiRefreshCw aria-hidden="true" />
      <div role="alert">
        <h2>작품을 불러오지 못했습니다.</h2>
        <p>연결 상태를 확인한 뒤 다시 시도해 주세요.</p>
      </div>
      <button type="button" onClick={onRetry}>작품 다시 불러오기</button>
    </div>
  </section>
);

const EmptyState = () => (
  <section className="showcase-state" aria-label="공개 작품 없음">
    <div className="showcase-state__panel" role="status">
      <FiImage aria-hidden="true" />
      <div>
        <h2>새로운 작품이 곧 공개됩니다.</h2>
        <p>아이들의 다음 프로젝트를 준비하고 있어요. 잠시 뒤 다시 들러 주세요.</p>
      </div>
    </div>
  </section>
);

const ShowcaseAnchorTargets = () => (
  <div className="showcase-anchor-targets" aria-hidden="true">
    <span id="showcase-all" className="showcase-anchor-target" />
  </div>
);

function ShowcasePage() {
  const [projects, setProjects] = useState([]);
  const [likedProjectIds, setLikedProjectIds] = useState(() => new Set());
  const [likePending, setLikePending] = useState({});
  const [likeErrors, setLikeErrors] = useState({});
  const [likeNotices, setLikeNotices] = useState({});
  const [status, setStatus] = useState('loading');
  const [requestVersion, setRequestVersion] = useState(0);
  const [previewProject, setPreviewProject] = useState(null);
  const isMountedRef = useRef(true);
  const likeRequestsRef = useRef(new Map());
  const showcasePageRef = useRef(null);
  const previewTriggerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    const likeRequests = likeRequestsRef.current;

    return () => {
      isMountedRef.current = false;
      likeRequests.forEach(({ controller, timeoutId }) => {
        clearTimeout(timeoutId);
        controller.abort();
      });
      likeRequests.clear();
    };
  }, []);

  useEffect(() => {
    let active = true;
    const { controller, timeoutId } = createTimedController();

    setStatus('loading');
    setLikePending({});
    setLikeErrors({});
    setLikeNotices({});

    Promise.all([
      fetchJson(GALLERY_ENDPOINT, controller.signal),
      fetchJson(LIKES_ENDPOINT, controller.signal),
    ])
      .then(([galleryPayload, likesPayload]) => {
        if (!Array.isArray(galleryPayload)) {
          throw new Error('Gallery payload must be an array');
        }

        const seenProjectIds = new Set();
        const safeProjects = galleryPayload.reduce((normalized, project) => {
          const safeProject = normalizeProject(project);
          if (!safeProject || seenProjectIds.has(safeProject.id)) return normalized;
          seenProjectIds.add(safeProject.id);
          normalized.push(safeProject);
          return normalized;
        }, []);
        safeProjects.sort((left, right) => (
          Date.parse(right.showcase_added_at) - Date.parse(left.showcase_added_at)
        ));
        const safeProjectIds = new Set(safeProjects.map((project) => project.id));
        const likedIds = Array.isArray(likesPayload?.liked_project_ids)
          ? likesPayload.liked_project_ids.filter((id) => safeProjectIds.has(id))
          : [];

        if (!active) return;
        setProjects(safeProjects);
        setLikedProjectIds(new Set(likedIds));
        setStatus('ready');
      })
      .catch(() => {
        controller.abort();
        if (active) setStatus('error');
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

    return () => {
      active = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [requestVersion]);

  const handleLike = useCallback(async (projectId) => {
    if (likedProjectIds.has(projectId) || likeRequestsRef.current.has(projectId)) return;

    const { controller, timeoutId } = createTimedController();
    likeRequestsRef.current.set(projectId, { controller, timeoutId });

    setLikePending((current) => ({ ...current, [projectId]: true }));
    setLikeErrors((current) => withoutKey(current, projectId));
    setLikeNotices((current) => withoutKey(current, projectId));

    try {
      const response = await fetch(
        `${GALLERY_ENDPOINT}/${encodeURIComponent(projectId)}/like`,
        {
          method: 'POST',
          cache: 'no-store',
          headers: { 'X-Narnia-Public-Like': '1' },
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new Error(`Like request failed with status ${response.status}`);
      }

      const result = await response.json();
      if (!isValidLikeResult(result, projectId)) {
        throw new Error('Like response was not accepted');
      }
      if (!isMountedRef.current) return;

      setProjects((current) => current.map((project) => (
        project.id === projectId
          ? {
            ...project,
            like_count: result.like_count,
          }
          : project
      )));
      setLikedProjectIds((current) => {
        const next = new Set(current);
        next.add(projectId);
        return next;
      });
      if (result.already_liked) {
        setLikeNotices((current) => ({
          ...current,
          [projectId]: '이미 좋아요한 작품입니다.',
        }));
      }
    } catch {
      if (isMountedRef.current) {
        setLikeErrors((current) => ({ ...current, [projectId]: true }));
      }
    } finally {
      clearTimeout(timeoutId);
      const activeRequest = likeRequestsRef.current.get(projectId);
      if (activeRequest?.controller === controller) {
        likeRequestsRef.current.delete(projectId);
      }
      if (isMountedRef.current) {
        setLikePending((current) => withoutKey(current, projectId));
      }
    }
  }, [likedProjectIds]);

  const retryGallery = useCallback(() => {
    setRequestVersion((version) => version + 1);
  }, []);

  const openPreview = useCallback((project, triggerElement) => {
    previewTriggerRef.current = triggerElement;
    setPreviewProject(project);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewProject(null);
  }, []);

  const sectionProps = {
    likedProjectIds,
    likePending,
    likeErrors,
    likeNotices,
    onLike: handleLike,
    onPreview: openPreview,
  };
  const hasCollections = status === 'ready' && projects.length > 0;

  return (
    <>
      <div ref={showcasePageRef} className="showcase-page">
        <Helmet>
          <title>작품전시관 | 나니아랩</title>
          <meta
            name="description"
            content="나니아랩 학생들이 AI와 함께 만들고 직접 공개한 프로그램을 만나보세요."
          />
          <link rel="canonical" href="https://narnia-lab.com/showcase" />
        </Helmet>

        <a className="showcase-skip-link" href="#showcase-all">작품 목록으로 건너뛰기</a>

        <main id="showcase-top">
          <section className="showcase-hero" aria-labelledby="showcase-hero-title">
            <div className="showcase-hero__ambient" aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
            <div className="showcase-shell showcase-hero__layout">
              <div className="showcase-hero__copy">
                <h1 id="showcase-hero-title">
                  아이들의 아이디어가<br />
                  <span>지금, 세상과 만납니다.</span>
                </h1>
                <p>
                  질문에서 시작해 AI와 함께 만들고, 실제 링크로 공개한 프로그램입니다.
                  마음에 드는 작품을 직접 체험하고 응원해 주세요.
                </p>
              </div>

              <div
                className="showcase-hero__count"
                aria-label={status === 'ready'
                  ? `현재 공개 중인 작품 ${projects.length}개`
                  : '현재 공개 중인 작품 집계 중'}
              >
                <span>NOW SHOWING</span>
                <strong>{status === 'ready' ? String(projects.length).padStart(2, '0') : '—'}</strong>
                <small>개의 작품이 지금 작동하고 있습니다.</small>
              </div>
            </div>
            <div className="showcase-shell showcase-hero__rail" aria-hidden="true">
              <span>QUESTION</span>
              <i />
              <span>BUILD</span>
              <i />
              <span>LAUNCH</span>
            </div>
          </section>

          {!hasCollections ? <ShowcaseAnchorTargets /> : null}
          {status === 'loading' ? <LoadingState /> : null}
          {status === 'error' ? <ErrorState onRetry={retryGallery} /> : null}
          {status === 'ready' && projects.length === 0 ? <EmptyState /> : null}

          {hasCollections ? (
            <div className="showcase-collections">
              <ShowcaseSection
                id="showcase-all"
                title="공유한 작품"
                description="학생들이 직접 골라 공유한 프로그램을 최근 공개된 순서대로 둘러보세요."
                projects={projects}
                indexLabel="01"
                metaLabel="LATEST PROJECTS"
                {...sectionProps}
              />
            </div>
          ) : null}
        </main>
      </div>

      {previewProject ? (
        <ThumbnailPreview
          project={previewProject}
          onClose={closePreview}
          backgroundRef={showcasePageRef}
          returnFocusRef={previewTriggerRef}
        />
      ) : null}
    </>
  );
}

export default ShowcasePage;
